<template>
    <FunnelStepContainer>
        <template #title>Erste Einschätzung</template>
        <div class="mx-auto mt-8 max-w-lg divide-y rounded-xl border border-slate-200 bg-slate-50">
            <div class="p-4">
                <h3 class="text-sm font-medium tracking-wide text-slate-900">Ihre Auswahl</h3>
                <div class="mt-4 grid grid-cols-2 gap-4">
                    <div
                        v-for="selection in lift_selections"
                        v-show="form[selection] !== 'other' && form[selection] !== 'unknown'"
                        class="flex items-center gap-2"
                        :key="selection"
                    >
                        <img :src="selection_options[form[selection]].img" alt="" class="h-10 w-10" />
                        <label class="text-sm text-sky-950">{{ selection_options[form[selection]].label }}</label>
                    </div>
                </div>
            </div>

            <div class="p-4">
                <h3 class="text-sm font-medium tracking-wide text-slate-900">Lift-Kosten</h3>
                <p class="mt-4 text-xs text-sky-950">
                    Die Kosten für einen Treppenlift hängen von der Art des Lifts, der Form der Treppe und der Anzahl
                    der Etagen ab.
                </p>
                <dl class="mt-4 flex items-baseline justify-between">
                    <dt class="text-sm tracking-wide text-slate-800">
                        {{ lift_label }}
                    </dt>
                    <dd class="text-slate-800">
                        <span class="text-sm">ab</span>
                        <span>{{ ' ' }}</span>
                        <span class="text-base font-semibold text-slate-700">
                            {{
                                Intl.NumberFormat('de-DE', {
                                    style: 'currency',
                                    currency: 'EUR',
                                    maximumSignificantDigits: 4,
                                }).format(lift_cost)
                            }}
                        </span>
                    </dd>
                </dl>
                <h3 class="mt-8 text-sm font-medium tracking-wide text-slate-900">Zuschüsse</h3>
                <p class="mt-4 text-xs text-sky-950">
                    Abhängig von Ihrer Situation können Sie mehrere Förderungen und Zuschüsse für den Einbau eines
                    Treppenlifts erhalten.
                </p>
                <div v-if="care_support > 0" class="mt-4 flex gap-2">
                    <div><ArrowLongRightIcon class="h-5 text-slate-700" /></div>
                    <div>
                        <h4 class="text-sm text-sky-900">Förderung für Pflegebedürftige</h4>
                        <p class="mt-2 text-xs">
                            Je pflegebedürftiger Person im Haushalt kann man einen Zuschuss von 4.000 € erhalten.
                        </p>
                        <div class="text-right text-base font-medium text-lime-800">
                            {{
                                Intl.NumberFormat('de-DE', {
                                    style: 'currency',
                                    currency: 'EUR',
                                    maximumSignificantDigits: 4,
                                }).format(care_support)
                            }}
                        </div>
                    </div>
                </div>
                <div v-if="form.social_care === 'yes'" class="mt-4 flex gap-2">
                    <div><ArrowLongRightIcon class="h-5 text-slate-700" /></div>
                    <div>
                        <h4 class="text-sm text-sky-900">Förderung für Sozialhilfeempfänger</h4>
                        <p class="mt-2 text-xs">
                            Wenn sie Sozialhilfeempfänger sind, können Sie unter Umständen den Einbau eines Treppenlifts
                            gefördert bekommen.
                        </p>
                        <div class="text-right text-base font-medium text-lime-800">Komplette Übernahme</div>
                    </div>
                </div>
                <div v-if="form.accident_dependent === 'yes'" class="mt-4 flex gap-2">
                    <div><ArrowLongRightIcon class="h-5 text-slate-700" /></div>
                    <div>
                        <h4 class="text-sm text-sky-900">Unfallabhängige Förderung</h4>
                        <p class="mt-2 text-xs">
                            Falls der Einbau eines Treppenlifts aufgrund eines Unfalls notwendig ist, können Sie eine
                            Förderung für die gesamten Kosten erhalten.
                        </p>
                        <div class="text-right text-base font-medium text-lime-800">Komplette Übernahme</div>
                    </div>
                </div>
                <div v-if="form.state" class="mt-4 flex gap-2">
                    <div><ArrowLongRightIcon class="h-5 text-slate-700" /></div>
                    <div class="w-full">
                        <h4 class="text-sm text-sky-900">Länderspezifische Förderung</h4>
                        <p class="mt-2 text-xs">
                            Je nach Bundesland ist eine zustätzliche Förderung möglich. Wir helfen Ihnen bei der
                            Beantragung.
                        </p>
                    </div>
                </div>
            </div>

            <div class="p-4">
                <h3 class="text-sm font-medium tracking-wide text-slate-900">Ihre Kosten</h3>
                <p class="mt-4 text-xs text-sky-950">
                    Die Kosten für den Einbau eines Treppenlifts können durch Förderungen und Zuschüsse reduziert
                    werden.
                </p>
                <dl class="mt-4 flex items-baseline justify-between">
                    <dt class="text-sm tracking-tight text-slate-900"></dt>
                    <dd
                        class="text-lime-700 underline decoration-lime-700 decoration-double decoration-2 underline-offset-8"
                    >
                        <span class="text-sm">Eigenanteil ab</span>
                        <span>{{ ' ' }}</span>
                        <span class="text-xl font-semibold text-lime-700">
                            {{
                                Intl.NumberFormat('de-DE', {
                                    style: 'currency',
                                    currency: 'EUR',
                                    maximumSignificantDigits: 4,
                                }).format(0)
                            }}
                        </span>
                    </dd>
                </dl>
            </div>
        </div>
        <div class="mt-8 text-center">
            <button
                @click="submitOption"
                class="rounded-xl bg-sky-50 px-3.5 py-2.5 text-base font-medium text-sky-900 shadow-sm ring-inset ring-sky-300 hover:bg-sky-50 hover:ring-2"
            >
                Jetzt Anbieter finden
            </button>
        </div>
    </FunnelStepContainer>
</template>

<script setup>
import { computed } from 'vue'
import FunnelStepContainer from '@/Components/Funnel/FunnelStepContainer.vue'
import { ArrowLongRightIcon } from '@heroicons/vue/24/outline'

const emit = defineEmits(['step-submitted'])

const props = defineProps({
    step: {
        type: Object,
        required: true,
    },
    form: {
        type: Object,
        required: true,
    },
})

const lift_label = computed(() => {
    let lift = props.form.stairlift_type === 'unknown' ? 'Lift' : selection_options[props.form.stairlift_type].label
    let stairs = props.form.stairlift_form === 'other' ? 'ungerade' : selection_options[props.form.stairlift_form].label

    return `${lift} für eine ${stairs} Treppe`
})

const lift_cost = computed(() => {
    return cost_estimation[props.form.stairlift_type][props.form.stairlift_form]
})

const care_support = computed(() => {
    return props.form.care_dependent_people * 4000
})

const lift_selections = ['stairlift_type', 'stairlift_location', 'stairlift_form', 'stairlift_floor']

const cost_estimation = {
    seated_lift: {
        straight: 3000,
        curved: 8000,
        curved_podium: 8000,
        other: 8000,
    },
    platform_lift: {
        straight: 9000,
        curved: 12000,
        curved_podium: 12000,
        other: 12000,
    },
    hub_lift: {
        straight: 5000,
        curved: 10000,
        curved_podium: 10000,
        other: 10000,
    },
}

const selection_options = {
    seated_lift: {
        label: 'Sitzlift',
        img: '/img/icons/sitzlift.png',
    },
    platform_lift: {
        label: 'Plattformlift',
        img: '/img/icons/plattform.png',
    },
    hub_lift: {
        label: 'Hublift',
        img: '/img/icons/lift.png',
    },
    unknown: {
        label: 'Weiß nicht',
        img: '/img/icons/unknown.png',
    },
    indoors: {
        label: 'Innen',
        img: '/img/icons/inside.png',
    },
    outdoors: {
        label: 'Außen',
        img: '/img/icons/outside.png',
    },
    indoors_outdoors: {
        label: 'Innen und Außen',
        img: '/img/icons/both.png',
    },
    straight: {
        label: 'Gerade Treppe',
        img: '/img/icons/straight.png',
    },
    curved: {
        label: 'Kurvige',
        img: '/img/icons/curvy.png',
    },
    curved_podium: {
        label: 'L-Form mit Podest',
        img: '/img/icons/podium.png',
    },
    other: {
        label: 'Andere Form',
        img: '/img/icons/other.png',
    },
    '1_floor': {
        label: 'Eine Etage',
        img: '/img/icons/1-etage.png',
    },
    '2_floor': {
        label: 'Zwei Etagen',
        img: '/img/icons/2-etage.png',
    },
    '2_plus_floor': {
        label: 'Mehr als zwei Etagen',
        img: '/img/icons/2-plus-etage.png',
    },
}

function submitOption() {
    let nextStep = props.step.next(props.form)
    emit('step-submitted', nextStep)
}
</script>
